import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { LIST } from "constants/index"

type WishListState = {
  listInfo: WishList | null;
  itemInfo: SingleWishItem | null;
  viewMode: ViewMode;
  currentItemIndex: number;
  currentItemComments: GetWishItemCommentsResponseDto | null;
  currentItemLikes: GetWishItemLikesResponseDto | null;
  showCommentsBottomSheet: boolean;
  showLikedByBottomSheet: boolean;
  showRollbackConfirmationDialog: boolean;
  showGeniesList: boolean;
  showSelectGeniesBottomSheet: boolean;
  genies: { [id: number]: UserDto[] } | null;
}

const initialState = {
  listInfo: null,
  itemInfo: null,
  viewMode: LIST,
  currentItemIndex: -1,
  currentItemComments: null,
  currentItemLikes: null,
  showCommentsBottomSheet: false,
  showLikedByBottomSheet: false,
  showRollbackConfirmationDialog: false,
  showGeniesList: false,
  showSelectGeniesBottomSheet: false,
  genies: null
} as WishListState

export const wishList = createSlice({
  name: "wishList",
  initialState: initialState,
  reducers: {
    updateWishListInfo: (state, action: PayloadAction<WishList | null>) => {
      state.listInfo = action.payload ? {
        ...action.payload,
        listItems: action.payload?.listItems
      } : null
    },
    updateWishListViewMode: (state, action: PayloadAction<ViewMode>) => {
      state.viewMode = action.payload
    },
    updateCurrentItemInfo: (state, action: PayloadAction<SingleWishItem | null>) => {
      state.itemInfo = action.payload ? structuredClone(action.payload) : null
    },
    updateCurrentItemIndex: (state, action: PayloadAction<number>) => {
      state.currentItemIndex = action.payload
      if (state.currentItemComments !== initialState.currentItemComments) {
        state.currentItemComments = initialState.currentItemComments
      }
      if (state.currentItemLikes !== initialState.currentItemLikes) {
        state.currentItemLikes = initialState.currentItemLikes
      }
    },
    updateCurrentItemComments: (state, action: PayloadAction<GetWishItemCommentsResponseDto | null>) => {
      state.currentItemComments = structuredClone(action.payload)
    },
    updateShowCommentsBottomSheet: (state, action: PayloadAction<boolean>) => {
      state.showCommentsBottomSheet = action.payload
    },
    updateShowLikedByBottomSheet: (state, action: PayloadAction<boolean>) => {
      state.showLikedByBottomSheet = action.payload
    },
    updateCurrentItemLikes: (state, action: PayloadAction<GetWishItemLikesResponseDto | null>) => {
      state.currentItemLikes = structuredClone(action.payload)
    },
    updateGenies: (state, action: PayloadAction<{ id: number, genies: UserDto[] }>) => {
      const newItemGenies = { [action.payload.id]: action.payload.genies }
      state.genies = state.genies ? {
        ...state.genies,
        ...newItemGenies
      } : newItemGenies
    },
    updateShowGeniesList: (state, action: PayloadAction<boolean>) => {
      state.showGeniesList = action.payload
    },
    updateShowSelectGeniesBottomSheet: (state, action: PayloadAction<boolean>) => {
      state.showSelectGeniesBottomSheet = action.payload
    },
    updateShowRollbackConfirmationDialog: (state, action: PayloadAction<boolean>) => {
      state.showRollbackConfirmationDialog = action.payload
    },
    resetWishListSlice: () => structuredClone(initialState)
  }
})

export const {
  updateWishListInfo,
  updateWishListViewMode,
  updateCurrentItemInfo,
  updateCurrentItemIndex,
  updateCurrentItemComments,
  updateShowCommentsBottomSheet,
  updateShowLikedByBottomSheet,
  updateCurrentItemLikes,
  updateGenies,
  updateShowGeniesList,
  updateShowSelectGeniesBottomSheet,
  updateShowRollbackConfirmationDialog,
  resetWishListSlice
} = wishList.actions

export default wishList.reducer
